import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { PageHeader, StatusBadge } from "@/components/common";
import CompanySelect from "@/components/CompanySelect";
import TaskDrawer from "@/components/TaskDrawer";
import { useAuth } from "@/context/AuthContext";
import { api, todayISO } from "@/lib/api";

const DOT = { not_started: "bg-slate-400", in_progress: "bg-blue-500", awaiting_client: "bg-amber-500", submitted: "bg-sky-500", under_review: "bg-teal-500", verified: "bg-emerald-600" };
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

export default function CalendarPage() {
  const { isStaff } = useAuth();
  const [month, setMonth] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [tenant, setTenant] = useState("");
  const [tasks, setTasks] = useState([]);
  const [selected, setSelected] = useState(todayISO());
  const [open, setOpen] = useState(null);
  const days = useMemo(() => {
    const start = new Date(month); start.setDate(1 - ((month.getDay() + 6) % 7));
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(start); d.setDate(start.getDate() + i); return d; });
  }, [month]);
  const load = () => api.get("/tasks", { params: { date_from: iso(days[0]), date_to: iso(days[41]), tenant_id: tenant || undefined } }).then((r) => setTasks(r.data));
  useEffect(() => { load(); }, [days, tenant]); // eslint-disable-line
  const byDay = useMemo(() => tasks.reduce((m, t) => ((m[t.due_date] = m[t.due_date] || []).push(t), m), {}), [tasks]);
  const shift = (n) => setMonth(new Date(month.getFullYear(), month.getMonth() + n, 1));
  const dayTasks = byDay[selected] || [];
  return (
    <div>
      <PageHeader title="Compliance calendar" subtitle="Deadlines by due date">{isStaff && <CompanySelect value={tenant} onChange={setTenant} allowAll testId="calendar-company-filter" />}</PageHeader>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="rounded-lg border bg-card p-4 lg:col-span-2" data-testid="compliance-calendar-view">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-heading text-lg font-bold" data-testid="calendar-month-label">{month.toLocaleDateString("en-GB", { month: "long", year: "numeric" })}</h2>
            <div className="flex gap-1">
              <Button size="icon" variant="outline" onClick={() => shift(-1)} data-testid="calendar-prev-btn"><ChevronLeft className="h-4 w-4" /></Button>
              <Button size="icon" variant="outline" onClick={() => shift(1)} data-testid="calendar-next-btn"><ChevronRight className="h-4 w-4" /></Button>
            </div>
          </div>
          <div className="grid grid-cols-7 gap-px overflow-hidden rounded-md border bg-border text-xs">
            {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <div key={d} className="bg-muted px-2 py-1.5 font-semibold text-muted-foreground">{d}</div>)}
            {days.map((d) => {
              const k = iso(d); const list = byDay[k] || []; const inMonth = d.getMonth() === month.getMonth();
              return (
                <button key={k} onClick={() => setSelected(k)} data-testid={`calendar-day-${k}`} className={`min-h-[64px] bg-card p-1.5 text-left transition-colors hover:bg-muted/50 sm:min-h-[88px] ${inMonth ? "" : "opacity-40"} ${selected === k ? "ring-2 ring-inset ring-primary" : ""}`}>
                  <span className={`inline-flex h-5 w-5 items-center justify-center rounded-full ${k === todayISO() ? "bg-primary text-white" : ""}`}>{d.getDate()}</span>
                  <div className="mt-1 hidden space-y-0.5 sm:block">{list.slice(0, 2).map((t) => <div key={t.id} className="flex items-center gap-1 truncate"><span className={`h-1.5 w-1.5 shrink-0 rounded-full ${DOT[t.status]}`} />{t.title}</div>)}{list.length > 2 && <div className="text-muted-foreground">+{list.length - 2} more</div>}</div>
                  <div className="mt-1 flex gap-0.5 sm:hidden">{list.slice(0, 4).map((t) => <span key={t.id} className={`h-1.5 w-1.5 rounded-full ${DOT[t.status]}`} />)}</div>
                </button>
              );
            })}
          </div>
        </div>
        <div className="rounded-lg border bg-card" data-testid="calendar-day-panel">
          <div className="border-b px-5 py-3 text-xs font-medium uppercase tracking-wider text-muted-foreground">Due {selected}</div>
          {!dayTasks.length ? <p className="p-5 text-sm text-muted-foreground">No deadlines on this day.</p> : dayTasks.map((t) => (
            <button key={t.id} onClick={() => setOpen(t.id)} className="flex w-full items-center justify-between gap-2 border-b px-5 py-3 text-left last:border-0 hover:bg-muted/40" data-testid={`calendar-task-${t.id}`}>
              <div className="min-w-0"><div className="truncate text-sm font-medium">{t.title}</div><div className="truncate text-xs text-muted-foreground">{t.company_name} · {t.period_key}</div></div>
              <StatusBadge status={t.status} />
            </button>
          ))}
        </div>
      </div>
      <TaskDrawer taskId={open} onClose={() => setOpen(null)} onChanged={load} />
    </div>
  );
}
